import { PaginationProps } from 'baseui/pagination'
import { useRouter } from 'next/dist/client/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

export const usePagination = (limit = 10, pageName = 'page') => {
  const router = useRouter()

  const queryPage = useMemo(() => {
    return router.query[pageName]
      ? Number(router.query[pageName])
      : Number(router.asPath.split(`?${pageName}=`)[1])
  }, [pageName, router.asPath, router.query])

  const [page, _setPage] = useState(isNaN(queryPage) ? 1 : queryPage)

  const setPage = useCallback(
    (nextPage: number) => {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          [pageName]: nextPage,
        },
      })
    },
    [pageName, router]
  )

  useEffect(() => {
    if (queryPage && queryPage !== page) {
      _setPage(queryPage)
    }
  }, [page, queryPage, setPage])

  const offset = useMemo(() => (page - 1) * limit, [limit, page])

  const [countTotal, setCountTotal] = useState(0)

  const onPageChange = useCallback(
    ({ nextPage }: { nextPage: number; prevPage: number }) => {
      setPage(nextPage)
    },
    [setPage]
  )

  const onPrevClick = useCallback(() => {
    if (page > 1) {
      setPage(page - 1)
    }
  }, [page, setPage])

  const onNextClick = useCallback(() => {
    if (page < Math.ceil(countTotal / limit)) {
      setPage(page + 1)
    }
  }, [countTotal, limit, page, setPage])

  const paginationProps: PaginationProps = useMemo(
    () => ({
      currentPage: page,
      numPages: Math.ceil(countTotal / limit),
      onPageChange,
      onNextClick,
      onPrevClick,
    }),
    [countTotal, limit, onNextClick, onPageChange, onPrevClick, page]
  )

  return {
    limit,
    offset,
    paginationProps,
    setCountTotal,
    setPage,
    countTotal,
  }
}
